import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {CurrentWeather} from '../../../shared/models/weather';
import {WeatherService} from '../../../core/services/weather.service';
import {Subscription} from 'rxjs';
import {SpinnerComponent} from '../spinner/spinner.component';

@Component({
  selector: 'motn-current-weather',
  templateUrl: './current-weather.component.html',
  styleUrls: ['./current-weather.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SpinnerComponent,
  ],
})
export class CurrentWeatherComponent implements OnInit, OnChanges, OnDestroy {
  @Input() postalCode: string;

  private weatherSub: Subscription;

  loadingWeather: boolean = true;
  currentWeather: CurrentWeather;
  weatherIcon: string; // stored as an array to get icon SVG to refresh
  unknownLocation: boolean = false;


  constructor(
    private weatherService: WeatherService,
  ) {
  }


  ngOnInit(): void {
    this.weatherSub = this.weatherService.currentWeather$.subscribe(
      (weather) => {
        if (weather) {
          if (!weather.Location.City) {
            this.unknownLocation = true;
          } else {
            this.unknownLocation = false;
          }

          this.currentWeather = weather;
          this.loadingWeather = false;
          this.renderWeatherIcon();
        }
      },
    );
  }


  /**
   * @param changes {SimpleChanges}
   * @see postalCode {string} The property inside changes below needs to be the
   *    same as the @Input property for this class. If the @Input's name changes,
   *    need to change it below too.
   */
  ngOnChanges(changes: SimpleChanges) {
    const previous = changes.postalCode?.previousValue;
    const current = changes.postalCode?.currentValue;

    if (current && previous !== current) {
      this.fetchWeather();
    }
  }


  ngOnDestroy() {
    this.weatherSub?.unsubscribe();
  }


  private fetchWeather() {
    if (!this.postalCode) {
      return;
    }

    this.weatherService.updateCurrentWeather(this.postalCode);
  }


  private renderWeatherIcon() {
    const isDay = this.currentWeather.IsDay;

    if (this.currentWeather.Condition.Text.toLowerCase().includes('sunny')) {
      this.weatherIcon = 'sun';
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('clear')) {
      this.weatherIcon = 'moon-stars';
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('partly cloudy')) {
      if (isDay) {
        this.weatherIcon = 'clouds-sun';
      } else {
        this.weatherIcon = 'clouds-moon';
      }
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('cloudy')) {
      this.weatherIcon = 'clouds';
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('overcast')) {
      this.weatherIcon = 'clouds';
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('mist')) {
      this.weatherIcon = 'cloud-drizzle';
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('patchy')) {
      if (isDay) {
        this.weatherIcon = 'cloud-sun-rain';
      } else {
        this.weatherIcon = 'cloud-moon-rain';
      }
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('thundery')) {
      if (isDay) {
        this.weatherIcon = 'cloud-bolt-sun';
      } else {
        this.weatherIcon = 'cloud-bolt-moon';
      }
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('thunder')) {
      this.weatherIcon = 'cloud-bolt';
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('fog')) {
      this.weatherIcon = 'cloud-fog';
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('drizzle')) {
      this.weatherIcon = 'cloud-drizzle';
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('rain')) {
      this.weatherIcon = 'cloud-rain';
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('sleet')) {
      this.weatherIcon = 'cloud-sleet';
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('snow')) {
      this.weatherIcon = 'cloud-snow';
      return;
    }
    if (this.currentWeather.Condition.Text.toLowerCase().includes('ice pellets')) {
      this.weatherIcon = 'cloud-hail';
      return;
    }
  }
}
